/**
 * AI chat intake body — shared by /projects/new/chat (page) and the calendar
 * modal. The assistant gathers the minimum to draft a project from a
 * conversation (+ pasted screenshots / attached PDFs); "Build draft" extracts a
 * <ProjectDraftReview> the user confirms. Page variant adds the route-based
 * IntakeHeader + server draft autosave; modal variant is ephemeral and seeded
 * from a calendar slot. A PDF→chat hand-off arrives via the `pdfHandoff` prop.
 */
import { useEffect, useRef, useState } from 'react';
import {
  ExternalLink,
  FileCheck2,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Search,
  Send,
  TriangleAlert,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import type { IntakeProposedTask, ProjectCreateInput } from '@vari-crew/schemas';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Textarea } from '@/components/ui/Input';
import { cn } from '@/lib/cn';
import { Markdown } from '@/components/ui/Markdown';
import {
  extractDoc,
  recordExtractionCorrections,
  useChatExtract,
  useIntakeChat,
  useIntakeMode,
  type ChatTurn,
  type ChatTurnFile,
} from '@/api/intake';
import { useCreateProject } from '@/api/projects';
import { deleteDraft } from '@/api/project-drafts';
import { useDraftAutosave } from '@/lib/useDraftAutosave';
import { IntakeHeader } from '@/components/intake/IntakeHeader';
import { uploadProjectDocument } from '@/api/attachments';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { ProjectDraftReview } from '@/components/intake/ProjectDraftReview';
import type { TimingModel } from '@/components/intake/timing/timingModel';
import { clearIntakeSession } from '@/lib/intakeSession';
import { draftToCreateInput } from '@/lib/intakeDraft';
import { diffDraftCorrections } from '@/lib/intakeCorrections';
import {
  applySeed,
  useIntakeState,
  type IntakePanelProps,
  type PdfChatHandoff,
} from '@/components/intake/intakePanelShared';

const SYSTEM_GREETING: ChatTurn = {
  role: 'model',
  text:
    "Hi — tell me about the request. Client name, where the install is, " +
    'and roughly when you need it. You can also paste a screenshot of a Teams ' +
    "chat or an email (or attach a sales order PDF) and I'll read it. We can " +
    'turn it into a project draft when you have enough details.',
};

// Small files ride inline (base64) so the model sees the actual image/PDF; they
// re-ride to Gemini on every turn, so the inline budget stays under Gemini's
// ~20 MB inline-request ceiling (base64 inflates raw bytes ~33%). Files past the
// inline cutoff are read ONCE into Markdown server-side (POST /intake/extract-doc)
// and carried as a compact digest — no re-ride, and the source can be far larger
// (the extractor uses Gemini's Files API past ~15 MB).
const MAX_FILES = 4;
const MAX_FILE_MB = 50; // extract ceiling (matches the document-attachment cap)
const INLINE_MAX_MB = 5; // ≤ this rides inline; larger is extracted to Markdown
const MAX_TOTAL_MB = 15; // bounds the inline bytes in one turn
const INLINE_MAX_BYTES = INLINE_MAX_MB * 1024 * 1024;

/** Read a File into raw base64 (strips the `data:<mime>;base64,` prefix). */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Turn a staged File into a chat-turn attachment: small files inline (base64,
 * vision preserved); larger files read once into a Markdown digest server-side
 * so the bytes never re-ride inline on later turns.
 */
async function fileToChatTurnFile(f: File): Promise<ChatTurnFile> {
  if (f.size <= INLINE_MAX_BYTES) {
    return {
      name: f.name,
      mime_type: f.type || 'application/octet-stream',
      data: await fileToBase64(f),
    };
  }
  const res = await extractDoc(f);
  return { name: res.name, mime_type: res.mime_type, extracted_text: res.markdown };
}

/**
 * Drop inline file bytes before persisting a draft — keep the draft small and
 * the turns valid (each file part keeps a text marker). The conversation + the
 * built draft survive a resume; only re-running the model on an inline image
 * would miss its pixels, which is fine (re-attach to include it again).
 */
function stripChatFiles(history: ChatTurn[]): ChatTurn[] {
  return history.map((t) =>
    !t.files?.length
      ? t
      : {
          ...t,
          files: t.files.map((f) =>
            f.extracted_text
              ? f
              : {
                  name: f.name,
                  mime_type: f.mime_type,
                  extracted_text: `[attached file: ${f.name} — re-attach to include the original]`,
                },
          ),
        },
  );
}

export function ChatIntakePanel({
  variant,
  seed,
  onCreated,
  onDirtyChange,
  pdfHandoff,
}: IntakePanelProps & { pdfHandoff?: PdfChatHandoff | null }) {
  const { data: mode } = useIntakeMode();
  const { data: me } = useCurrentUser();
  const isPm = me?.role === 'pm';
  // Supervisors/admins create projects directly and plan the crew's tasks
  // inline; PMs are requesters and never see a task editor.
  const canEditTasks = me?.role === 'supervisor' || me?.role === 'admin';
  const persist = variant === 'page';
  const kp = persist ? 'chat' : 'cal.chat';
  const chat = useIntakeChat();
  const extract = useChatExtract();
  const create = useCreateProject();
  // Persisted across screen switches for the session (page); ephemeral (modal).
  const [history, setHistory] = useIntakeState<ChatTurn[]>(persist, `${kp}.history`, [
    SYSTEM_GREETING,
  ]);
  const [input, setInput] = useIntakeState(persist, `${kp}.input`, '');
  const [draft, setDraft] = useIntakeState<Partial<ProjectCreateInput> | null>(
    persist,
    `${kp}.draft`,
    null,
  );
  const [draftSource, setDraftSource] = useIntakeState<'stub' | 'gemini' | undefined>(
    persist,
    `${kp}.draftSource`,
    undefined,
  );
  // The draft exactly as the model extracted it, kept alongside the editable
  // copy so we can capture the PM's corrections as eval pairs (EX.9 / D.6).
  const [originalDraft, setOriginalDraft] = useIntakeState<Partial<ProjectCreateInput> | null>(
    persist,
    `${kp}.draft.original`,
    null,
  );
  // User-turn count at the last successful build — lets the user keep chatting
  // after building and rebuild to fold the new details into the draft.
  const [builtTurns, setBuiltTurns] = useIntakeState<number>(persist, `${kp}.builtTurns`, 0);
  // Server-draft id (survives screen switches); set on first autosave / resume.
  const [draftId, setDraftId] = useIntakeState<string | null>(persist, `${kp}.draftId`, null);
  // Editable task plan (supervisor/admin only) — sent as proposed_tasks.
  const [proposedTasks, setProposedTasks] = useIntakeState<IntakeProposedTask[]>(
    persist,
    `${kp}.proposedTasks`,
    [],
  );
  // Supervisor "Plan the work" authoring model — persisted (session cache + draft)
  // so per-block roles/times/descriptions survive a screen switch / resumed draft.
  const [timingModel, setTimingModel] = useIntakeState<TimingModel | null>(
    persist,
    `${kp}.timingModel`,
    null,
  );
  // Files staged in the composer, and every file accepted into the
  // conversation (uploaded to the project on create).
  const [pendingFiles, setPendingFiles] = useIntakeState<File[]>(persist, `${kp}.pendingFiles`, []);
  const [insertedFiles, setInsertedFiles] = useIntakeState<File[]>(
    persist,
    `${kp}.insertedFiles`,
    [],
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const seededRef = useRef<PdfChatHandoff | null>(null);
  // True while staged files are being read/extracted before a send — large
  // attachments take a moment to transcribe to Markdown server-side.
  const [preparing, setPreparing] = useState(false);
  // True from the moment a submit starts, so autosave pauses and the unmount
  // flush can't re-create the draft we're about to delete.
  const [submitting, setSubmitting] = useState(false);
  const userTurns = history.filter((t) => t.role === 'user').length;
  // A draft exists but the conversation has moved on since it was built.
  const hasNewSinceBuild = !!draft && userTurns > builtTurns;

  // Report unsaved content so the modal can guard an accidental close.
  const dirty = userTurns > 0 || !!input.trim() || pendingFiles.length > 0 || !!draft;
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  // Autosave a resumable draft to the server (page only; survives a tab close).
  const draftSig = `${history.length}|${input}|${draft ? JSON.stringify(draft) : ''}|${builtTurns}|${proposedTasks.length}|${JSON.stringify(timingModel)}`;
  const saveState = useDraftAutosave({
    source: 'chat',
    draftId,
    setDraftId,
    signature: draftSig,
    paused: !persist || submitting,
    build: () => ({
      payload: {
        history: stripChatFiles(history),
        input,
        draft,
        draftSource,
        'draft.original': originalDraft,
        proposedTasks,
        timingModel,
        builtTurns,
      },
      hints: {
        // Before "Build" there's no extracted draft — name from the first
        // message so the draft isn't just "Untitled chat".
        project_title:
          draft?.title ??
          history.find((t) => t.role === 'user' && t.text.trim())?.text?.slice(0, 140) ??
          null,
        kind: (draft?.kind as string | undefined) ?? null,
        client: draft?.client_name ?? null,
        deadline: draft?.deadline_date ?? null,
        item_count: userTurns,
        built: !!draft,
      },
      hasContent: userTurns > 0,
    }),
  });

  // Paste a Teams/email screenshot straight into the composer (§B). Clipboard
  // images arrive as unnamed "image.png" parts, so give them a unique name —
  // otherwise the addFiles same-name+size dedup would drop a second paste.
  function onPasteFiles(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imgs: File[] = [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it && it.kind === 'file' && it.type.startsWith('image/')) {
        const f = it.getAsFile();
        if (f) {
          imgs.push(
            f.name && f.name !== 'image.png'
              ? f
              : new File([f], `pasted-${Date.now()}.png`, { type: f.type }),
          );
        }
      }
    }
    if (imgs.length > 0) {
      e.preventDefault(); // don't also paste the image's name as text
      addFiles(imgs);
      toast.success(`Added ${imgs.length} pasted screenshot${imgs.length > 1 ? 's' : ''}.`);
    }
  }

  function addFiles(incoming: File[]) {
    setPendingFiles((prev) => {
      const next = [...prev];
      for (const f of incoming) {
        if (next.length >= MAX_FILES) {
          toast.warning(`Up to ${MAX_FILES} files per message.`);
          break;
        }
        if (!(f.type.startsWith('image/') || f.type === 'application/pdf')) {
          toast.warning(`${f.name}: only images and PDFs can be attached.`);
          continue;
        }
        if (f.size > MAX_FILE_MB * 1024 * 1024) {
          toast.warning(`${f.name} is too large (max ${MAX_FILE_MB} MB).`);
          continue;
        }
        if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
        // Only inline-bound files (≤ INLINE_MAX) count toward the inline budget;
        // larger ones are extracted to Markdown server-side and don't re-ride.
        const inlineTotal = [...next, f]
          .filter((x) => x.size <= INLINE_MAX_BYTES)
          .reduce((s, x) => s + x.size, 0);
        if (inlineTotal > MAX_TOTAL_MB * 1024 * 1024) {
          toast.warning(`Too many inline attachments — add large files one at a time.`);
          break;
        }
        next.push(f);
      }
      return next;
    });
  }

  useEffect(() => {
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [history, chat.isPending]);

  // Hand-off from the PDF method (IH.C): seed the conversation with a recap +
  // the source files so the assistant can keep going. Files that fit the chat
  // caps are attached for the model to re-read; all of them persist on create.
  useEffect(() => {
    if (!pdfHandoff?.files?.length) return;
    if (seededRef.current === pdfHandoff) return;
    seededRef.current = pdfHandoff;
    setInsertedFiles(pdfHandoff.files);
    void (async () => {
      const fit: File[] = [];
      let total = 0;
      for (const f of pdfHandoff.files) {
        if (fit.length >= MAX_FILES) break;
        if (f.size > MAX_FILE_MB * 1024 * 1024) continue;
        if (total + f.size > MAX_TOTAL_MB * 1024 * 1024) break;
        fit.push(f);
        total += f.size;
      }
      let chatFiles: ChatTurnFile[] = [];
      try {
        chatFiles = await Promise.all(fit.map(fileToChatTurnFile));
      } catch {
        chatFiles = [];
      }
      setHistory([
        SYSTEM_GREETING,
        {
          role: 'user',
          text: `Continuing from my documents: ${pdfHandoff.files.map((f) => f.name).join(', ')}.`,
          ...(chatFiles.length ? { files: chatFiles } : {}),
        },
        { role: 'model', text: pdfHandoff.summary },
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfHandoff]);

  async function send() {
    const text = input.trim();
    const filesForTurn = pendingFiles;
    if (!text && filesForTurn.length === 0) return;
    if (preparing || chat.isPending) return;

    let files: ChatTurnFile[] | undefined;
    if (filesForTurn.length > 0) {
      setPreparing(true);
      try {
        // Small files inline; larger ones are read once into Markdown so they
        // don't re-ride inline on every later turn.
        files = await Promise.all(filesForTurn.map(fileToChatTurnFile));
      } catch (err) {
        toast.error(`Couldn't read an attachment: ${(err as Error).message}`);
        setPreparing(false);
        return;
      }
      setPreparing(false);
    }

    const userTurn: ChatTurn = { role: 'user', text, ...(files ? { files } : {}) };
    const next: ChatTurn[] = [...history, userTurn];
    setHistory(next);
    setInput('');
    setPendingFiles([]);
    try {
      // The greeting is purely UI; the API only needs real turns (user +
      // model). Drop the greeting before sending so it doesn't poison the
      // assistant's context with "I said this" messages.
      const apiHistory = next
        .filter((_, i) => i !== 0 || history[0] !== SYSTEM_GREETING)
        // Citations are display-only — don't send a model turn's sources back.
        .map((t) => ({ role: t.role, text: t.text, ...(t.files ? { files: t.files } : {}) }));
      const r = await chat.mutateAsync({
        history: apiHistory.length > 0 ? apiHistory : [userTurn],
      });
      setHistory((h) => [...h, { role: 'model', text: r.reply, sources: r.sources }]);
      // Remember accepted files so they persist to the project on create.
      if (filesForTurn.length > 0) {
        setInsertedFiles((prev) => [...prev, ...filesForTurn]);
      }
    } catch (err) {
      toast.error(`Chat failed: ${(err as Error).message}`);
      setHistory((h) => h.slice(0, -1)); // restore prompt so user can retry
      setInput(text);
      setPendingFiles(filesForTurn);
    }
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void send();
    }
  }

  async function buildDraft() {
    // The greeting is UI-only; send just the real conversation turns.
    const apiHistory = history.filter((t) => t !== SYSTEM_GREETING);
    if (apiHistory.length === 0) return;
    const isRebuild = !!draft;
    try {
      const r = await extract.mutateAsync({ history: apiHistory });
      if (!r.draft) {
        toast.warning('Not enough detail yet — mention the client and site, then try again.');
        return;
      }
      // Overlay the calendar slot's schedule so the drawn day/time shows in the
      // editable timing fields (the slot wins over anything the AI extracted).
      // originalDraft stays the raw AI output (correction telemetry ground truth).
      setDraft(applySeed(r.draft, seed));
      setOriginalDraft(r.draft);
      setDraftSource(r.source);
      // Mark where the conversation stood, so further chat re-enables rebuild.
      setBuiltTurns(userTurns);
      // Flexible task creation (Leo, demo #7): don't auto-seed the kind's default
      // tasks — the studio starts from whatever the supervisor has (empty → one
      // bare skeleton) and they opt in to the defaults via "Suggest tasks for this
      // kind". (A rebuild no longer clobbers the supervisor's authored plan.)
      if (isRebuild) toast.success('Draft rebuilt from the conversation.');
    } catch (err) {
      toast.error(`Couldn't build a draft: ${(err as Error).message}`);
    }
  }

  function patchDraft<K extends keyof ProjectCreateInput>(
    key: K,
    value: Partial<ProjectCreateInput>[K],
  ) {
    setDraft((d) => ({ ...(d ?? {}), [key]: value }));
  }

  async function createFromDraft() {
    // The slot was overlaid onto `draft` when it was built, so submit as-is —
    // re-applying here would clobber timing the user adjusted in the review.
    const res = draftToCreateInput(draft, 'ai_chat');
    if (!res.ok) {
      toast.error(res.error);
      return;
    }
    try {
      setSubmitting(true); // pause autosave through submit → delete → navigate
      const created = await create.mutateAsync({
        ...res.value,
        // Supervisor's edited task plan — sent EXPLICITLY, even when empty, so an
        // intentionally task-less plan creates zero tasks (flexible task creation)
        // rather than the server re-deriving the kind default. PMs send none
        // (omit) → server derives the default from the kind.
        ...(canEditTasks ? { proposed_tasks: proposedTasks } : {}),
      });

      // Capture the PM's edits to the AI extraction as eval pairs (EX.9 / D.6).
      // Best-effort, real extractions only.
      if (draftSource === 'gemini') {
        const corrections = diffDraftCorrections(originalDraft, draft);
        if (corrections.length > 0) {
          void recordExtractionCorrections({
            project_id: created.id,
            channel: 'chat',
            extraction_source: 'gemini',
            corrections,
          }).catch(() => {});
        }
      }

      // Persist any files inserted during the conversation as project
      // documents (best-effort); the server guesses kind from the filename.
      let docFailures = 0;
      for (const f of insertedFiles) {
        try {
          await uploadProjectDocument({ projectId: created.id, file: f });
        } catch {
          docFailures += 1;
        }
      }

      const noun = isPm ? 'Request submitted' : 'Project created';
      const docOk = insertedFiles.length - docFailures;
      if (docFailures > 0) {
        toast.warning(
          `${noun}${docOk > 0 ? ` with ${docOk} document${docOk > 1 ? 's' : ''}` : ''}, but ${docFailures} document(s) couldn't be attached — add them from the project.`,
        );
      } else if (docOk > 0) {
        toast.success(`${noun} with ${docOk} document${docOk > 1 ? 's' : ''} · ${created.title}`);
      } else {
        toast.success(`${noun} · ${created.title}`);
      }
      // The project exists now — drop its draft (best-effort, page only).
      if (persist) {
        if (draftId) void deleteDraft(draftId).catch(() => {});
        clearIntakeSession('chat');
      }
      onDirtyChange?.(false);
      onCreated(created);
    } catch (err) {
      setSubmitting(false);
      toast.error(`Failed to create project: ${(err as Error).message}`);
    }
  }

  return (
    <>
      {variant === 'page' && <IntakeHeader mode="chat" isPm={isPm} save={saveState} />}

      {mode?.stub && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2 text-xs">
          <TriangleAlert className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
          <div className="text-amber-900">
            <span className="font-semibold">Stub mode.</span> Replies are scripted heuristics, not
            Gemini. Set <code className="font-mono">GOOGLE_GENAI_API_KEY</code> to enable live
            conversational reasoning.
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-0 flex flex-col h-[60vh]">
          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {history.map((turn, i) => (
              <ChatBubble key={i} turn={turn} />
            ))}
            {chat.isPending && (
              <div className="flex items-center gap-2 text-xs text-ink-faint">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
              </div>
            )}
          </div>
          <div className="border-t border-line p-3 space-y-2">
            {pendingFiles.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {pendingFiles.map((f, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 rounded-md border border-line bg-fill-soft px-2 py-1 text-[11px]"
                  >
                    {f.type.startsWith('image/') ? (
                      <ImageIcon className="w-3 h-3 text-ink-faint" />
                    ) : (
                      <FileText className="w-3 h-3 text-ink-faint" />
                    )}
                    <span className="max-w-[160px] truncate">{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setPendingFiles((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label={`Remove ${f.name}`}
                      className="text-ink-faint hover:text-ink"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(Array.from(e.target.files ?? []));
                  e.target.value = '';
                }}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => fileInputRef.current?.click()}
                disabled={chat.isPending || preparing}
                title="Attach a screenshot, photo, or PDF"
                aria-label="Attach a screenshot, photo, or PDF"
                className="shrink-0"
              >
                <Paperclip className="w-3.5 h-3.5" />
              </Button>
              <Textarea
                rows={2}
                placeholder="Type a message, or paste a Teams/email screenshot…"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPasteFiles}
                className="resize-none"
              />
              <Button
                onClick={() => void send()}
                disabled={chat.isPending || preparing || (!input.trim() && pendingFiles.length === 0)}
                className="shrink-0"
              >
                {preparing ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Reading…
                  </>
                ) : (
                  <>
                    <Send className="w-3.5 h-3.5" />
                    Send
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {userTurns > 0 && (
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {hasNewSinceBuild && (
            <span className="text-xs text-status-requested-fg">
              New messages since the draft — rebuild to fold them in.
            </span>
          )}
          <Button
            onClick={() => void buildDraft()}
            disabled={extract.isPending}
            // Subtle once a draft is already in sync; prominent to (re)build.
            variant={draft && !hasNewSinceBuild ? 'outline' : 'default'}
          >
            {extract.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FileCheck2 className="w-3.5 h-3.5" />
            )}
            {draft ? 'Rebuild draft from conversation' : 'Build draft from this conversation'}
          </Button>
        </div>
      )}

      {draft && (
        <ProjectDraftReview
          draft={draft}
          onPatch={patchDraft}
          onSubmit={createFromDraft}
          submitLabel={isPm ? 'Submit request' : 'Create project'}
          submitting={create.isPending}
          source={draftSource}
          heading="Review draft from chat"
          proposedTasks={canEditTasks ? proposedTasks : undefined}
          onProposedTasksChange={canEditTasks ? setProposedTasks : undefined}
          initialTimingModel={canEditTasks ? timingModel : undefined}
          onTimingModelChange={canEditTasks ? setTimingModel : undefined}
          allowSignoff={canEditTasks}
          onReset={() => {
            setDraft(null);
            setOriginalDraft(null);
            setDraftSource(undefined);
            setProposedTasks([]);
            setTimingModel(null);
            setBuiltTurns(0);
          }}
        />
      )}
    </>
  );
}

function ChatBubble({ turn }: { turn: ChatTurn }) {
  const isUser = turn.role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'rounded-2xl px-3 py-2 text-sm max-w-[80%]',
          isUser
            ? 'bg-sky-600 text-white rounded-tr-sm whitespace-pre-wrap'
            : 'bg-fill-soft text-ink rounded-tl-sm border border-line',
        )}
      >
        {turn.files && turn.files.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-1.5">
            {turn.files.map((f, i) => (
              <span
                key={i}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px]',
                  isUser ? 'bg-white/20' : 'border border-line bg-bg',
                )}
              >
                {f.mime_type.startsWith('image/') ? (
                  <ImageIcon className="w-3 h-3" />
                ) : (
                  <FileText className="w-3 h-3" />
                )}
                <span className="max-w-[160px] truncate">{f.name}</span>
              </span>
            ))}
          </div>
        )}
        {isUser ? turn.text : <Markdown>{turn.text}</Markdown>}
        {!isUser && turn.sources && turn.sources.length > 0 && (
          <div className="mt-2 border-t border-line pt-2">
            <div className="mb-1 flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-ink-faint">
              <Search className="h-3 w-3" /> Web sources
            </div>
            <ul className="space-y-0.5">
              {turn.sources.map((s, i) => (
                <li key={i}>
                  <a
                    href={s.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-accent hover:underline"
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate max-w-[260px]">{s.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
